/*
 * BOARDROOM CONSENSUS QUEUE — central nervous system. Every agent routes outputs,
 * flags, briefs and approval requests here. Oracle synthesizes ONE briefing per
 * cycle. Sovereign: approve · retract · redirect · defer. Always visible.
 */
import { useCallback, useEffect, useState } from "react";
import { Scale, Loader2, Sunrise, RefreshCw } from "lucide-react";
import { osApi } from "@/lib/api-client";
import { toast } from "sonner";

const KIND_CLS = {
  anomaly: "text-rose-300 border-rose-500/40",
  drift_flag: "text-rose-300 border-rose-500/40",
  decision_gate: "text-rose-300 border-rose-500/40",
  venture_brief: "text-amber-400 border-amber-500/40",
  content_proposal: "text-sky-300 border-sky-500/40",
  intel_brief: "text-sky-300 border-sky-500/40",
  pow_summary: "text-white/50 border-white/20",
};

export const ConsensusQueue = ({ refreshKey }) => {
  const [data, setData] = useState(null);
  const [briefing, setBriefing] = useState(null);
  const [redirecting, setRedirecting] = useState(null);
  const [instruction, setInstruction] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    osApi.consensus().then(setData).catch(() => {});
    osApi.briefing().then(setBriefing).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("uc:consensus-refresh", h);
    return () => window.removeEventListener("uc:consensus-refresh", h);
  }, [load]);

  const act = async (id, action, extra = {}) => {
    try {
      const r = await osApi.act(id, action, extra);
      toast.success(`${action.toUpperCase()} recorded${r.executed ? ` — ${r.executed.replace(/_/g, " ")}` : ""}`);
      setRedirecting(null); setInstruction("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${action} failed`);
    }
  };

  const regenBriefing = async () => {
    setBusy(true);
    try { setBriefing(await osApi.briefingRegen()); toast.success("Oracle briefing re-synthesized"); }
    catch { toast.error("Briefing synthesis failed"); } finally { setBusy(false); }
  };

  return (
    <div className="border border-white/10 bg-black/40 p-4" data-testid="consensus-queue">
      <div className="flex items-center justify-between mb-3">
        <div className="font-mono-uc text-[10px] uppercase tracking-widest text-white/50 flex items-center gap-1.5">
          <Scale size={11} className="text-amber-400" /> Consensus Queue
          {data && <span className="text-amber-400" data-testid="consensus-open-count">· {data.open} open</span>}
        </div>
        <button onClick={regenBriefing} disabled={busy}
          className="uc-chip text-amber-400 hover:bg-amber-500/10 flex items-center gap-1.5"
          data-testid="briefing-regen-btn">
          {busy ? <Loader2 size={10} className="animate-spin" /> : <Sunrise size={10} />} Synthesize Briefing
        </button>
      </div>

      {briefing && (
        <div className="border border-amber-500/30 bg-amber-500/5 p-3 mb-3" data-testid="oracle-briefing">
          <div className="font-mono-uc text-[9px] uppercase text-amber-400 mb-1">
            Oracle briefing · {briefing.date} · {briefing.polished_by}
          </div>
          <div className="text-xs text-white/85 leading-relaxed max-h-28 overflow-y-auto uc-scroll">
            {briefing.spoken_text}
          </div>
        </div>
      )}

      {!data ? <Loader2 className="animate-spin text-white/40" size={16} /> : (
        <div className="space-y-2 max-h-[420px] overflow-y-auto uc-scroll">
          {data.items.length === 0 && (
            <div className="text-white/40 text-xs p-3 text-center" data-testid="consensus-empty">
              Queue clear. Silence from an agent means it is working.
            </div>
          )}
          {data.items.map((i) => (
            <div key={i.id} className={`border bg-black/50 p-3 ${KIND_CLS[i.kind] || "border-white/10"}`}
              data-testid={`consensus-item-${i.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <span className="font-mono-uc text-[9px] uppercase mr-2">{i.agent}</span>
                  <span className={`font-mono-uc text-[9px] uppercase mr-2 px-1 border ${i.risk_tier === "T3" ? "text-rose-300 border-rose-500/40" : i.risk_tier === "T2" ? "text-amber-400 border-amber-500/40" : "text-white/40 border-white/20"}`}>{i.risk_tier}</span>
                  <span className="text-xs text-white">{i.title}</span>
                </div>
                <div className="flex gap-1.5">
                  <button onClick={() => act(i.id, "approve")} className="uc-chip text-emerald-300 hover:bg-emerald-500/10"
                    data-testid={`approve-${i.id}`}>Approve</button>
                  <button onClick={() => act(i.id, "retract")} className="uc-chip text-rose-300 hover:bg-rose-500/10"
                    data-testid={`retract-${i.id}`}>Retract</button>
                  <button onClick={() => setRedirecting(redirecting === i.id ? null : i.id)}
                    className="uc-chip text-sky-300 hover:bg-sky-500/10" data-testid={`redirect-${i.id}`}>Redirect</button>
                  <button onClick={() => act(i.id, "defer")} className="uc-chip text-white/40"
                    data-testid={`defer-${i.id}`}>Defer</button>
                </div>
              </div>
              <div className="text-[11px] text-white/55 mt-1">{i.body?.slice(0, 220)}</div>
              <div className="font-mono-uc text-[9px] text-white/30 mt-1">
                source: {(i.source_refs || []).slice(0, 2).join(" · ")} · {(i.created_at || "").slice(0, 16)}
              </div>
              {redirecting === i.id && (
                <div className="flex gap-2 mt-2">
                  <input value={instruction} onChange={(e) => setInstruction(e.target.value)}
                    placeholder="Sovereign instruction for the agent…"
                    className="flex-1 bg-black/60 border border-white/15 p-1.5 text-xs text-white outline-none focus:border-sky-500/50"
                    data-testid={`redirect-input-${i.id}`} />
                  <button onClick={() => act(i.id, "redirect", { instruction })}
                    disabled={!instruction.trim()}
                    className="uc-chip text-sky-300 disabled:opacity-40" data-testid={`redirect-send-${i.id}`}>
                    Send
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
